/**
 * Photos and pictures of pages, picked together, made into one PDF to be read as a scan.
 *
 * A page photographed with a phone is read as a scanned PDF is: the pictures are uploaded,
 * the server makes them into one PDF, a page for each picture in the order of their names
 * (`POST /theory/pictures`; docs/06, "Reading a scan"), and that PDF is what is drawn and
 * read from then on. A PDF picked alone is read as it is, and one picked among pictures has its
 * pages put in its place.
 */

import { PICTURES_LIMIT, UPLOAD_LIMIT } from "@app/shared-types";
import { api } from "../api/client";
import { bytes } from "./format";
import { isPdf, type ScanProgress } from "./scan-read";

/** What a picker offers for the pages of a score: a PDF, or photos and pictures of them. */
export const PAGES_ACCEPT = ".pdf,application/pdf,image/*,.heic,.heif";

/** A file's first bytes, enough to tell a PDF and every picture apart from a score. */
async function head(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(0, 12).arrayBuffer());
}

function starts(data: Uint8Array, bytes: readonly number[], at = 0): boolean {
  return bytes.every((byte, k) => data[at + k] === byte);
}

const text = (said: string): number[] => [...said].map((one) => one.charCodeAt(0));

/** Whether `data` begins a picture a page may be photographed or scanned as, by its bytes. */
function isPicture(data: Uint8Array): boolean {
  return (
    starts(data, [0xff, 0xd8, 0xff]) || // JPEG
    starts(data, [0x89, 0x50, 0x4e, 0x47]) || // PNG
    starts(data, text("GIF8")) ||
    starts(data, text("BM")) ||
    starts(data, [0x49, 0x49, 0x2a, 0x00]) || // TIFF, little-endian
    starts(data, [0x4d, 0x4d, 0x00, 0x2a]) || // TIFF, big-endian
    (starts(data, text("RIFF")) && starts(data, text("WEBP"), 8)) ||
    // HEIC, HEIF and AVIF, as phones take them: an ISO box naming its type.
    starts(data, text("ftyp"), 4)
  );
}

/** Whether `file` is a page: a PDF, or a photo or a picture of one. */
export async function isPage(file: File): Promise<boolean> {
  const data = await head(file);
  return isPdf(data) || isPicture(data);
}

/** Whether `files` are one PDF, read as it is, rather than photos to be made into one. */
export async function isLonePdf(files: readonly File[]): Promise<boolean> {
  const [only] = files;
  return files.length === 1 && only !== undefined && isPdf(await head(only));
}

/** `files` in the order of their names, as a camera or a scanner numbers them. */
export function ordered(files: readonly File[]): File[] {
  return [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
  );
}

/** What files picked together are called: the first one's name, and how many more. */
export function called(files: readonly File[]): string {
  const [first] = ordered(files);
  if (first === undefined) return "";
  return files.length === 1 ? first.name : `${first.name} and ${files.length - 1} more`;
}

/**
 * `files`, every one a page, as one PDF to read, in the order of their names; a PDF picked
 * alone as it is. `null` when the page stopped waiting for it; `told` that they are being sent.
 */
export async function asPdf(
  files: readonly File[],
  live: () => boolean,
  told: (progress: ScanProgress) => void,
): Promise<File | null> {
  const pages = ordered(files);
  const [first] = pages;
  if (first === undefined) throw new Error("no file was picked");
  if (await isLonePdf(pages)) return first;
  if (pages.length > PICTURES_LIMIT) {
    throw new Error(`${pages.length} were picked; at most ${PICTURES_LIMIT} are read as one score`);
  }
  const large = pages.find((one) => one.size > UPLOAD_LIMIT);
  if (large !== undefined) {
    throw new Error(
      `${large.name} is ${bytes(large.size)}; a picture may run to ${bytes(UPLOAD_LIMIT)}`,
    );
  }
  told({ stage: "uploading" });
  const uploads = await Promise.all(pages.map(async (one) => (await api.upload(one)).upload));
  // Not taken by anything if this page has stopped waiting: the server sweeps them.
  if (!live()) return null;
  const pdf = await api.pictures({ uploads, names: pages.map((one) => one.name) });
  return live() ? new File([pdf], called(pages), { type: "application/pdf" }) : null;
}
